import { useState, useEffect, type JSX } from 'react'
import { useNavigate } from 'react-router'
import { Column } from 'primereact/column'
import AdminListScreenLayout from '../../../components/layouts/AdminListScreenLayout.js'
import DataTable from '../../../components/ui/DataTable.js'
import { useSmsGateways } from '../../../hooks/useSmsGateways.js'
import { useConfirmDialog } from '../../../components/ui/ConfirmDialog.js'
import { MessageSquare, Eye, Pencil, Trash2 } from 'lucide-react'
import SummaryCards from '../../../components/admin/SummaryCards.js'
import ListToolbar from '../../../components/admin/ListToolbar.js'
import { formatDateTime } from '../../../utils/formatDate.js'
export default function SmsGatewaysPage(): JSX.Element {
  const navigate = useNavigate()
  const { items, loading, refetch, pagination, summary, remove } = useSmsGateways()
  const [searchQuery, setSearchQuery] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize] = useState(10)
  const { confirm, Dialog } = useConfirmDialog()

  useEffect(() => {
    void refetch(searchQuery, currentPage, pageSize)
  }, [refetch, searchQuery, currentPage, pageSize])

  const handleAdd = () => {
    navigate('/admin/channels/sms-gateways/new')
  }

  const handleEdit = (id: string) => {
    navigate(`/admin/channels/sms-gateways/${id}`)
  }

  const handleView = (id: string) => {
    navigate(`/admin/channels/sms-gateways/${id}?view=true`)
  }

  const handleDelete = async (id: string) => {
    confirm({
      title: 'Delete SMS Gateway',
      message: 'Are you sure you want to delete this SMS gateway?',
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
      onConfirm: async () => {
        try {
          await remove(id)
          void refetch(searchQuery, currentPage, pageSize)
        } catch (error) {
          console.error('Error deleting SMS gateway:', error)
        }
      },
    })
  }

  const actionsBodyTemplate = (rowData: { id: string }): JSX.Element => {
    return (
      <div className="flex items-center gap-1">
        <button
          onClick={() => handleView(rowData.id)}
          className="h-7 w-7 flex items-center justify-center rounded-full border border-gray-100 text-gray-400 hover:border-transparent hover:text-white hover:bg-[#3454cf] transition-all shrink-0"
          title="View"
        >
          <Eye className="h-3.5 w-3.5" />
        </button>
        <button
          onClick={() => handleEdit(rowData.id)}
          className="h-7 w-7 flex items-center justify-center rounded-full border border-gray-100 text-gray-400 hover:border-transparent hover:text-white hover:bg-[#3454cf] transition-all shrink-0"
          title="Edit"
        >
          <Pencil className="h-3.5 w-3.5" />
        </button>
        <button
          onClick={() => handleDelete(rowData.id)}
          className="h-7 w-7 flex items-center justify-center rounded-full border border-gray-100 text-gray-400 hover:border-transparent hover:text-white hover:bg-red-500 transition-all shrink-0"
          title="Delete"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    )
  }

  const statusBodyTemplate = (rowData: { status: boolean }): JSX.Element => {
    return (
      <span className={`px-2 py-1 rounded text-xs ${rowData.status ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
        {rowData.status ? 'Active' : 'Inactive'}
      </span>
    )
  }

  const isDefaultBodyTemplate = (rowData: { isDefault: boolean }): JSX.Element => {
    return (
      <span className={`px-2 py-1 rounded text-xs ${rowData.isDefault ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`}>
        {rowData.isDefault ? 'Default' : '-'}
      </span>
    )
  }

  const providerBodyTemplate = (rowData: { provider: string }): JSX.Element => {
    const providerLabels: Record<string, string> = {
      pldt: 'PLDT',
      mobile360: 'Mobile 360',
      twilio: 'Twilio',
      yeastar: 'Yeastar',
    }
    return <span>{providerLabels[rowData.provider] || rowData.provider}</span>
  }

  return (
    <AdminListScreenLayout>
      <div className="w-full space-y-6 px-4 pb-10">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 tracking-tight">SMS Gateways</h1>
            <p className="text-sm text-gray-400 mt-1">Configure and manage SMS gateway providers.</p>
          </div>
          <button
            onClick={handleAdd}
            className="inline-flex items-center gap-2 bg-[#3454cf] hover:bg-[#2a45b0] text-white text-sm font-semibold px-5 py-2.5 rounded-sm transition-all shadow-md"
          >
            <MessageSquare className="h-4 w-4" />
            Add SMS Gateway
          </button>
        </div>

        {/* Summary cards */}
        {summary && (
          <SummaryCards
            cards={[
              { label: 'Total SMS Gateways', value: summary.total, accentColor: '#6366f1' },
              { label: 'Active', value: summary.active, accentColor: '#10b981' },
              { label: 'Inactive', value: summary.inactive, accentColor: '#ef4444' },
              { label: 'Default', value: summary.default, accentColor: '#f59e0b' },
            ]}
          />
        )}

        {/* Toolbar */}
        <ListToolbar
          searchQuery={searchQuery}
          onSearchChange={(value) => { setSearchQuery(value); setCurrentPage(1) }}
          onRefresh={() => { setSearchQuery(''); setCurrentPage(1); void refetch('', 1, pageSize) }}
          allButtonLabel="All SMS Gateways"
          placeholder="Search SMS gateways"
          loading={loading}
        />

        {/* Table */}
        <DataTable value={items} loading={loading} emptyMessage="No SMS gateways found." className="shadow-[0_2px_12px_rgba(0,0,0,0.04)] rounded-sm border-gray-100">
          <Column
            header="SMS Gateway"
            body={(rowData: any) => (
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-sm bg-[#3454cf]/5 flex items-center justify-center shrink-0">
                  <MessageSquare className="h-5 w-5 text-[#3454cf]" />
                </div>
                <p className="text-sm font-semibold text-gray-900">{rowData.name}</p>
              </div>
            )}
            style={{ width: '25%' }}
          />
          <Column
            field="provider"
            header="Provider"
            body={providerBodyTemplate}
          />
          <Column
            field="status"
            header="Status"
            body={statusBodyTemplate}
          />
          <Column
            field="isDefault"
            header="Default"
            body={isDefaultBodyTemplate}
          />
          <Column
            field="createdAt"
            header="Created At"
            body={(rowData: any) => formatDateTime(rowData.createdAt)}
          />
          <Column
            field="updatedAt"
            header="Updated At"
            body={(rowData: any) => formatDateTime(rowData.updatedAt)}
          />
          <Column
            field="actions"
            header="Actions"
            body={actionsBodyTemplate}
            style={{ width: '120px' }}
          />
        </DataTable>

        {/* Pagination */}
        {pagination && pagination.total > 0 && (
          <div className="flex items-center justify-between mt-4">
            <div className="text-sm text-gray-500">
              Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, pagination.total)} of {pagination.total} SMS gateway records
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => { setCurrentPage(p => Math.max(1, p - 1)); void refetch(searchQuery, Math.max(1, currentPage - 1), pageSize) }}
                disabled={currentPage === 1}
                className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                Previous
              </button>
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                  let pageNum
                  if (pagination.totalPages <= 5) {
                    pageNum = i + 1
                  } else if (currentPage <= 3) {
                    pageNum = i + 1
                  } else if (currentPage >= pagination.totalPages - 2) {
                    pageNum = pagination.totalPages - 4 + i
                  } else {
                    pageNum = currentPage - 2 + i
                  }
                  return (
                    <button
                      key={pageNum}
                      onClick={() => { setCurrentPage(pageNum); void refetch(searchQuery, pageNum, pageSize) }}
                      className={`px-3 py-1.5 text-sm font-medium rounded-sm transition-all ${
                        currentPage === pageNum
                          ? 'bg-[#3454cf] text-white'
                          : 'text-gray-700 bg-white border border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  )
                })}
              </div>
              <button
                onClick={() => { setCurrentPage(p => Math.min(pagination.totalPages, p + 1)); void refetch(searchQuery, Math.min(pagination.totalPages, currentPage + 1), pageSize) }}
                disabled={currentPage === pagination.totalPages}
                className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
      <Dialog />
    </AdminListScreenLayout>
  )
}
